import { useMemo } from 'react';
import ToggleButtonGroup from '@creditflowresearch/uilib/ToggleButtonGroup';
import type { ToggleButtonGroupTheme } from '@creditflowresearch/uilib/ToggleButtonGroup';
import Tooltip from '@creditflowresearch/uilib/Tooltip';
import IconLock from '@creditflowresearch/uilib/Icon/IconLock';
import LockedFeaturePopup from '@/creditflowresearch/ui/common/components/molecules/LockedFeaturePopup';
import type { IFilterButton } from './types';

import styles from './buttonGroup.module.scss';

export interface IButtonGroupProps {
  items: IFilterButton[],
  onClick?: (arg: IFilterButton) => void,
  /** `finsight` sits on a colored panel, `light` on a white one. */
  theme?: ToggleButtonGroupTheme,
  /** Names the group for assistive tech; pages render several of these side by side. */
  ariaLabel?: string,
}

const getItemValue = (item: IFilterButton): string => item.key || item.title;

const ButtonGroup = ({
  items,
  onClick,
  theme = 'finsight',
  ariaLabel,
}: IButtonGroupProps) => {
  const activeValues = useMemo(
    () => items.filter((item) => item.isActive).map(getItemValue),
    [items],
  );

  const handleChange = (value: string): void => {
    const item = items.find((option) => getItemValue(option) === value);

    // Restricted items open the upgrade popup instead of applying the filter.
    if (!item?.hasAccess) {
      return;
    }

    (onClick || item.onClick)?.(item);
  };

  return (
    <ToggleButtonGroup
      value={ activeValues }
      onChange={ handleChange }
      size="S"
      theme={ theme }
      ariaLabel={ ariaLabel }
      className={ styles.buttonSetWrapper }
      dataTest="buttonSetWrapper"
    >
      { items.map((item) => {
        const value = getItemValue(item);

        const button = (
          <ToggleButtonGroup.Item
            key={ value }
            value={ value }
            title={ item.title }
            dataTest={ item.dataTest || item.title }
          >
            { !item.hasAccess && <IconLock className={ styles.iconLock } data-test="lockIcon" /> }
            { item.title }
          </ToggleButtonGroup.Item>
        );

        if (!item.hasAccess) {
          return (
            <LockedFeaturePopup
              key={ value }
              name={ item.title }
              content={ item.tooltip }
            >
              { button }
            </LockedFeaturePopup>
          );
        }

        return item.tooltip ? (
          <Tooltip key={ value } content={ item.tooltip }>{ button }</Tooltip>
        ) : button;
      }) }
    </ToggleButtonGroup>
  );
};

export default ButtonGroup;
